import React, { useState, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import { HTTP_SERVER_URL } from '../../configuration';

export const ModalType = 'MANAGE_BOOKINGS_MODAL';

interface Booking {
  id: number;
  provider_id: string;
  username: string;
  realname: string;
  slot_time: string;
  help: boolean;
}

function formatTime(isoString: string): string {
  const d = new Date(isoString);
  const hh = d.getHours().toString().padStart(2, '0');
  const mm = d.getMinutes().toString().padStart(2, '0');
  return `${hh}:${mm}`;
}

function getToken(): string {
  const raw = localStorage.getItem('Token');
  return raw ? JSON.parse(raw).token : '';
}

export default (props: any): JSX.Element => {
  const queueName: string = props.queueName;

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [jsonText, setJsonText] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!props.show) return;
    fetch(`${HTTP_SERVER_URL}/api/queues/${queueName}/bookings`, {
      headers: { Authorization: `Token ${getToken()}` }
    })
      .then(r => r.json())
      .then((data: Booking[]) => setBookings(data))
      .catch(() => setBookings([]));
  }, [props.show, queueName]);

  function handleUpload(): void {
    setError('');
    setSuccess('');
    let parsed: any[];
    try {
      parsed = JSON.parse(jsonText);
      if (!Array.isArray(parsed)) throw new Error('Expected a JSON array');
    } catch (e) {
      setError(`Invalid JSON: ${(e as any).message}`);
      return;
    }

    setLoading(true);
    fetch(`${HTTP_SERVER_URL}/api/queues/${queueName}/bookings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Token ${getToken()}`
      },
      body: JSON.stringify(parsed)
    })
      .then(r => {
        if (!r.ok) throw new Error(`Server error: ${r.status}`);
        return r.json();
      })
      .then((data: { error: string | null, bookings: Booking[], skipped: string[] }) => {
        setBookings(data.bookings);
        setJsonText('');
        if (data.error === 'unresolved_identifiers') {
          setError(`${data.skipped.length} identifier(s) could not be resolved: ${data.skipped.join(', ')}. The remaining bookings were uploaded.`);
        }
        const uploaded = data.bookings.length;
        if (uploaded > 0) {
          setSuccess(`${uploaded} booking(s) uploaded.`);
        }
      })
      .catch((e: any) => setError(e.message))
      .finally(() => setLoading(false));
  }

  function handleClear(): void {
    setLoading(true);
    fetch(`${HTTP_SERVER_URL}/api/queues/${queueName}/bookings`, {
      method: 'DELETE',
      headers: { Authorization: `Token ${getToken()}` }
    })
      .then(() => setBookings([]))
      .catch((e: any) => setError(e.message))
      .finally(() => setLoading(false));
  }

  return (
    <Modal
      {...props}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered>
      <Modal.Header closeButton>
        <Modal.Title id="contained-modal-title-vcenter">
          Manage bookings — {queueName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <h6>Current bookings ({bookings.length})</h6>
        {bookings.length === 0
          ? <p className="text-muted">No bookings for this queue.</p>
          : <table className="table table-sm table-bordered">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Username</th>
                  <th>Time</th>
                  <th>Type</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map(b => (
                  <tr key={b.id}>
                    <td>{b.realname}</td>
                    <td>{b.username}</td>
                    <td>{formatTime(b.slot_time)}</td>
                    <td>{b.help ? 'help' : 'present'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
        }

        <hr />

        <h6>Upload bookings</h6>
        <p className="text-muted" style={{fontSize: '0.85em'}}>
          Paste a JSON array of objects with <code>provider_id</code>, <code>slot_time</code> (ISO 8601),
          and <code>entry_type</code> (<code>"help"</code> or <code>"present"</code>).
          This replaces all existing bookings for the queue.
        </p>
        <textarea
          className="w-100 form-control"
          rows={6}
          placeholder={'[\n  {"provider_id": "u1abc123", "slot_time": "2026-03-01T10:00:00Z", "entry_type": "help"}\n]'}
          value={jsonText}
          onChange={e => setJsonText(e.target.value)} />
        {success && <p className="text-success mt-1">{success}</p>}
        {error && <p className="text-danger mt-1">{error}</p>}
      </Modal.Body>
      <Modal.Footer>
        <button
          className="btn btn-danger mr-auto"
          onClick={handleClear}
          disabled={loading}>
          Clear all bookings
        </button>
        <button
          className="btn btn-primary"
          onClick={handleUpload}
          disabled={loading || !jsonText.trim()}>
          {loading ? 'Uploading…' : 'Upload bookings'}
        </button>
      </Modal.Footer>
    </Modal>
  );
};
